import { useContext, useEffect } from "react";
import {
  Route,
  type RouteComponentProps,
  Switch,
  useLocation,
} from "react-router-dom";
import { useI18nContext } from "@hopper-b2b/i18n";
import { B2B_PORTAL_AUTH_REDIRECT_TO } from "@hopper-b2b/types";
import { B2BLoadingPopup } from "@hopper-b2b/ui";
import queryStringParser from "query-string";

import {
  startSession,
  type StartSessionResponse,
  type UberSessionInfo,
} from "../../api/session/startSession";

import "./styles.scss";
import { UserContext } from "../../App";
import {
  PATH_AUTH,
  PATH_AUTH_INVALID_SESSION,
  PATH_AUTH_LOGOUT,
  PATH_HOME,
} from "../../utils/urlPaths";
import ExpiredSession from "../ExpiredSession/component";
import {
  SESSION_TOKEN_PARAM,
  LOGOUT_LOADING_MESSAGE,
  LOGOUT_REDIRECT,
  REDIRECT_URI_PARAM,
  INITIATOR_PARAM,
  SESSION_ID_TOKEN_PARAM,
} from "./constants";

export const StartSessionComponent = ({ history }) => {
  const { t } = useI18nContext();
  const location = useLocation();
  const { updateSessionInfo } = useContext(UserContext);

  const redirectToSessionStorageUrl = (url: string) => {
    history.replace(url);
    sessionStorage.removeItem(B2B_PORTAL_AUTH_REDIRECT_TO);
  };

  const handleRedirect = (
    response: StartSessionResponse,
    redirectUri: string
  ) => {
    const sessionInfo: UberSessionInfo = {
      hopperSessionToken: response.hopperSessionToken,
      uberAccessToken: response.uberAccessToken,
      uberRefreshToken: response.uberRefreshToken,
      userInfoResponse: response.userInfoResponse,
      hopperUserId: response.hopperUserId,
      hopperUserIdToken: response.hopperUserIdToken,
      kustomerAccessToken: response.kustomerAccessToken,
    };

    updateSessionInfo?.(sessionInfo);

    const redirectTo =
      redirectUri || sessionStorage.getItem(B2B_PORTAL_AUTH_REDIRECT_TO);
    if (redirectTo) redirectToSessionStorageUrl(redirectTo);
    else history.replace(PATH_HOME);
  };

  const authGenericCodeFlow = async () => {
    // The start session request is made with the provided uber auth token
    // which is validated by the BFF before establishing a Hopper session
    try {
      const searchParams = new URLSearchParams(location?.search);

      const idToken = searchParams.get(SESSION_ID_TOKEN_PARAM);
      /**
       * @deprecated
       */
      let sessionToken = searchParams.get(SESSION_TOKEN_PARAM);

      const redirectUri =
        searchParams.get(REDIRECT_URI_PARAM) || "/flights/search";
      const decodedRedirectUri = decodeURIComponent(redirectUri);

      // initiator represents the redirect_uri used when generating an auth code
      const initiator = searchParams.get(INITIATOR_PARAM);
      const decodedInitiator = initiator
        ? decodeURIComponent(initiator)
        : undefined;

      //TODO: Removing this restricts code from working as a valid token
      if (sessionToken === null) {
        sessionToken = searchParams.get("code");
      }

      /**
       * @deprecated
       */
      const decodedSessionCode = sessionToken
        ? decodeURIComponent(sessionToken)
        : sessionToken;

      const decodedIDToken = idToken ? decodeURIComponent(idToken) : idToken;

      if (decodedSessionCode || decodedIDToken) {
        const response = await startSession({
          idToken: decodedIDToken,
          code: decodedSessionCode,
          initiator: decodedInitiator,
        });
        if (
          response.userInfoResponse.mobileNumber ||
          response.userInfoResponse.email
        ) {
          handleRedirect(response, decodedRedirectUri);
        } else {
          throw Error("Invalid response from startSession");
        }
      } else {
        throw Error("Invalid url params in startSession");
      }
    } catch (err) {
      history.push(PATH_AUTH_INVALID_SESSION);
    }
  };

  useEffect(() => {
    const queryString = queryStringParser.parse(location.search);

    if (queryString) authGenericCodeFlow();
    else history.push(PATH_AUTH_INVALID_SESSION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <B2BLoadingPopup
      open
      message={t("loading")}
      popupSize={"mobile"}
      fullScreen
    />
  );
};

export const InvalidSessionComponent = () => <ExpiredSession />;

export const LogoutComponent = ({ history }) => {
  const logout = async () => {
    try {
      // TODO: add logout api call
      //   const logoutResponse = await postLogout();
      //   if (logoutResponse.destinationUrl)
      //   window.location.replace(logoutResponse.destinationUrl);
      history.push(LOGOUT_REDIRECT);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <B2BLoadingPopup
      open
      message={LOGOUT_LOADING_MESSAGE}
      popupSize={"mobile"}
      fullScreen
    />
  );
};

export const AuthModule = (_props: RouteComponentProps) => {
  return (
    <Switch>
      <Route
        exact
        path={PATH_AUTH_INVALID_SESSION}
        render={(props: RouteComponentProps) => (
          <InvalidSessionComponent history={props.history} />
        )}
      />
      <Route
        exact
        path={PATH_AUTH_LOGOUT}
        render={(props: RouteComponentProps) => (
          <LogoutComponent history={props.history} />
        )}
      />
      <Route
        path={PATH_AUTH}
        render={(props: RouteComponentProps) => (
          <StartSessionComponent history={props.history} />
        )}
      />
    </Switch>
  );
};
